import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { ChatState, Chat, Message, ApiResponse } from '../types';

interface ChatContextType extends ChatState {
  createChat: (firstMessage?: string) => Promise<Chat | null>;
  selectChat: (chatId: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  searchChats: (query: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  favoriteChat: (chatId: string) => Promise<void>;
  clearCurrentChat: () => void;
  clearError: () => void;
  reloadChats: () => Promise<void>;
}

type ChatAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CHATS'; payload: Chat[] }
  | { type: 'ADD_CHAT'; payload: Chat }
  | { type: 'UPDATE_CHAT'; payload: Chat }
  | { type: 'REMOVE_CHAT'; payload: string }
  | { type: 'SET_CURRENT_CHAT'; payload: Chat | null }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'UPDATE_MESSAGE'; payload: Message }
  | { type: 'UPDATE_MESSAGE_BY_ID'; payload: { id: string; updates: Partial<Message> } }
  | { type: 'REPLACE_MESSAGE'; payload: { tempId: string; newMessage: Message } }
  | { type: 'SET_HAS_MORE'; payload: boolean }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_SEARCH_RESULTS'; payload: Chat[] }
  | { type: 'CLEAR_ERROR' };

const initialState: ChatState = {
  chats: [],
  currentChat: null,
  messages: [],
  loading: false,
  error: null,
  hasMore: true,
  searchQuery: '',
  searchResults: [],
};

const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CHATS':
      return { ...state, chats: action.payload };
    case 'ADD_CHAT':
      return { ...state, chats: [action.payload, ...state.chats] };
    case 'UPDATE_CHAT':
      return {
        ...state,
        chats: state.chats.map((chat) =>
          chat.id === action.payload.id ? { ...chat, ...action.payload } : chat
        ),
        currentChat: state.currentChat?.id === action.payload.id
          ? { ...state.currentChat, ...action.payload }
          : state.currentChat,
      };
    case 'REMOVE_CHAT':
      return {
        ...state,
        chats: state.chats.filter((chat) => chat.id !== action.payload),
        currentChat: state.currentChat?.id === action.payload ? null : state.currentChat,
      };
    case 'SET_CURRENT_CHAT':
      return { ...state, currentChat: action.payload };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
        // Update last message in current chat if it exists
        currentChat: state.currentChat ? {
          ...state.currentChat,
          lastMessage: action.payload,
          updatedAt: new Date().toISOString(),
        } : state.currentChat,
      };
    case 'UPDATE_MESSAGE':
      const updatedMessages = state.messages.map((msg) =>
        msg.id === action.payload.id ? { ...msg, ...action.payload } : msg
      );
      return {
        ...state,
        messages: updatedMessages,
        // Update last message in current chat if it's the updated message
        currentChat: state.currentChat?.lastMessage?.id === action.payload.id ? {
          ...state.currentChat,
          lastMessage: { ...state.currentChat.lastMessage, ...action.payload },
        } : state.currentChat,
      };
    case 'UPDATE_MESSAGE_BY_ID':
      const updatedMessagesById = state.messages.map((msg) =>
        msg.id === action.payload.id ? { ...msg, ...action.payload.updates } : msg
      );
      return {
        ...state,
        messages: updatedMessagesById,
        // Update last message in current chat if it's the updated message
        currentChat: state.currentChat?.lastMessage?.id === action.payload.id ? {
          ...state.currentChat,
          lastMessage: { ...state.currentChat.lastMessage, ...action.payload.updates },
        } : state.currentChat,
      };
    case 'REPLACE_MESSAGE':
      const replacedMessages = state.messages.map(msg =>
        msg.id === action.payload.tempId ? action.payload.newMessage : msg
      );
      return {
        ...state,
        messages: replacedMessages,
        currentChat: state.currentChat
          ? {
              ...state.currentChat,
              lastMessage:
                state.currentChat.lastMessage?.id === action.payload.tempId
                  ? action.payload.newMessage
                  : state.currentChat.lastMessage,
            }
          : state.currentChat,
      };
    case 'SET_HAS_MORE':
      return { ...state, hasMore: action.payload };
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    case 'SET_SEARCH_RESULTS':
      return { ...state, searchResults: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // Load chats on mount
  useEffect(() => {
    loadChats();
  }, []);

  const loadChats = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Load from UserConversation table (saved history)
      const conversationsResponse = await apiService.getUserConversations({
        page: 1,
        limit: 50,
        status: 'active',
      });

      if (conversationsResponse.success && conversationsResponse.data) {
        // Convert UserConversations to Chat format for the UI
        const chatsFromHistory = conversationsResponse.data.conversations.map((conv: any) => ({
          id: (conv.chatId?._id || conv.chatId || conv._id), // Extract chatId properly
          title: conv.title,
          type: 'ai_chat' as const,
          status: conv.status,
          messageCount: conv.messageCount || 0,
          lastMessage: conv.lastMessage,
          createdAt: conv.createdAt,
          updatedAt: conv.updatedAt,
          isBookmarked: conv.isBookmarked || false,
          tags: conv.tags || [],
          // Add required Chat properties with defaults
          user: conv.user || '',
          lastMessageAt: conv.updatedAt,
          aiModel: 'gpt-3.5-turbo',
          aiSettings: {
            temperature: 0.7,
            maxTokens: 2048,
            systemPrompt: '',
          },
          analytics: {
            messageCount: conv.messageCount || 0,
            totalTokens: 0,
            averageResponseTime: 0,
            totalMessages: conv.messageCount || 0,
            totalTokensUsed: 0,
            lastActiveAt: conv.updatedAt,
          },
          // Add missing Chat properties
          settings: {
            isPrivate: false,
            allowExport: true,
            retentionDays: 30,
          },
          isFavorite: conv.isBookmarked || false,
          // Add conversation-specific data
          conversationId: conv._id,
          summary: conv.summary,
        }));

        dispatch({ type: 'SET_CHATS', payload: chatsFromHistory });
      } else {
        // Fallback to regular chats if user conversations fail
        const response = await apiService.getChats({ limit: 50 });
        if (response.success && response.data) {
          dispatch({ type: 'SET_CHATS', payload: response.data.chats || [] });
        } else {
          dispatch({ type: 'SET_ERROR', payload: 'Failed to load chats' });
        }
      }
    } catch (error: any) {
      console.error('Error loading chats:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load chats' });
    }
  };

  const createChat = async (firstMessage?: string): Promise<Chat | null> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Generate a title from the first message if provided
      const title = firstMessage
        ? (firstMessage.length > 50 ? firstMessage.substring(0, 50) + '...' : firstMessage)
        : 'New Chat';

      const response = await apiService.createChat({ title });
      console.log('createChat API response:', response);

      if (response.success && response.data && response.data.chat) {
        const newChat = response.data.chat;
        console.log('createChat returned chat object:', newChat);
        dispatch({ type: 'ADD_CHAT', payload: newChat });
        dispatch({ type: 'SET_CURRENT_CHAT', payload: newChat });
        dispatch({ type: 'SET_MESSAGES', payload: [] });
        dispatch({ type: 'SET_LOADING', payload: false });
        return newChat;
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create chat (no chat object returned)' });
        console.error('createChat failed, response:', response);
        return null;
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create chat (exception)' });
      console.error('createChat exception:', error);
      return null;
    }
  };

  const selectChat = async (chatId: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Find chat in current list
      const chat = state.chats.find((c) => c.id === chatId);
      if (chat) {
        dispatch({ type: 'SET_CURRENT_CHAT', payload: chat });
      }

      // Try to load from UserConversation first (saved history)
      if (chat && (chat as any).conversationId) {
        try {
          const conversationResponse = await apiService.getUserConversation((chat as any).conversationId) as any;

          if (conversationResponse.success && conversationResponse.data) {
            const conversation = conversationResponse.data.conversation;

            // Convert conversation messages to Message format
            const messages = conversation.messages.map((msg: any) => {
              // Determine the sender - ensure AI responses are properly identified
              let sender: 'user' | 'assistant' | 'system';
              if (msg.role === 'user') {
                sender = 'user';
              } else if (msg.role === 'assistant' || msg.role === 'ai') {
                sender = 'assistant';
              } else if (msg.role === 'system') {
                sender = 'system';
              } else {
                // Default to assistant for any other role to ensure feedback works
                sender = 'assistant';
              }

              return {
                id: msg.id || msg._id,
                chat: chatId,
                sender,
                content: msg.content,
                type: 'text' as const,
                format: 'plain' as const,
                tokensUsed: msg.metadata?.tokensUsed || 0,
                status: 'sent' as const,
                attachments: [],
                metadata: {
                  ...(msg.metadata || {}),
                  // Ensure we have the role in metadata for reference
                  role: msg.role || 'assistant'
                },
                reactions: [],
                threadMessages: [],
                editHistory: [],
                isEdited: false,
                isDeleted: false,
                isPinned: false,
                isImportant: false,
                createdAt: msg.timestamp || msg.createdAt,
                updatedAt: msg.timestamp || msg.updatedAt,
              };
            });

            dispatch({ type: 'SET_MESSAGES', payload: messages });
            dispatch({ type: 'SET_HAS_MORE', payload: false }); // Conversations are complete
            return;
          }
        } catch (convError) {
          console.log('Failed to load from conversation, trying regular messages:', convError);
        }
      }

      // Fallback to regular message loading
      const response = await apiService.getMessages(chatId, { limit: 50 });

      if (response.success && response.data) {
        const messages = response.data.messages || [];
        dispatch({ type: 'SET_MESSAGES', payload: messages });
        dispatch({ type: 'SET_HAS_MORE', payload: messages.length === 50 });
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load messages' });
      }
    } catch (error: any) {
      console.error('Error selecting chat:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load messages' });
    }
  };

  const sendMessage = async (content: string): Promise<void> => {
    let currentChat = state.currentChat;

    // If no chat is selected, create a new one and wait for it
    if (!currentChat || !currentChat.id) {
      const newChat = await createChat(content);
      console.log('createChat returned:', newChat);
      if (!newChat || typeof newChat !== 'object') {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create chat. No chat object returned.' });
        return;
      }
      if (!('id' in newChat) || !newChat.id) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create chat. Chat object missing id.' });
        return;
      }
      currentChat = newChat;
    }

    const chatId = currentChat.id;
    if (!chatId) {
      dispatch({ type: 'SET_ERROR', payload: 'Chat ID is missing. Cannot send message.' });
      return;
    }
    const messageId = 'msg_' + Date.now();
    const aiMessageId = 'ai_' + Date.now();

    try {
      // Add optimistic message
      const optimisticMessage: Message = {
        id: messageId,
        chat: chatId,
        sender: 'user',
        content,
        type: 'text',
        format: 'plain',
        tokensUsed: 0,
        status: 'sending',
        attachments: [],
        metadata: { device: 'desktop' },
        reactions: [],
        threadMessages: [],
        editHistory: [],
        isEdited: false,
        isDeleted: false,
        isPinned: false,
        isImportant: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Add user message immediately
      dispatch({ type: 'ADD_MESSAGE', payload: optimisticMessage });

      // Create thinking message to show while waiting for AI response
      const thinkingMessage: Message = {
        id: aiMessageId,
        chat: chatId,
        sender: 'assistant',
        content: '•••', // Three dots that will be animated
        type: 'text',
        format: 'plain',
        tokensUsed: 0,
        status: 'sending',
        attachments: [],
        metadata: {
          device: 'desktop',
          isSimulated: true,
          isStreaming: true,
          isThinking: true,
        },
        reactions: [],
        threadMessages: [],
        editHistory: [],
        isEdited: false,
        isDeleted: false,
        isPinned: false,
        isImportant: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Add thinking message immediately
      dispatch({ type: 'ADD_MESSAGE', payload: thinkingMessage });

      // Timeout logic
      let connectionTimeout: NodeJS.Timeout | null = null;
      let responseTimeout: NodeJS.Timeout | null = null;
      let timedOut = false;

      // Connection timeout (10s)
      const connectionPromise = new Promise((_, reject) => {
        connectionTimeout = setTimeout(() => {
          timedOut = true;
          dispatch({
            type: 'UPDATE_MESSAGE_BY_ID',
            payload: {
              id: aiMessageId,
              updates: {
                content: 'Not connected to API.',
                status: 'failed',
                updatedAt: new Date().toISOString(),
                metadata: { device: 'desktop', isThinking: false },
              },
            },
          });
          dispatch({ type: 'SET_LOADING', payload: false });
          reject(new Error('Not connected to API'));
        }, 10000);
      });

      // Response timeout (90s)
      const responsePromise = new Promise((_, reject) => {
        responseTimeout = setTimeout(() => {
          timedOut = true;
          console.log('⏰ 90-second timeout fired: API request timed out after 90 seconds.');
          dispatch({
            type: 'UPDATE_MESSAGE_BY_ID',
            payload: {
              id: aiMessageId,
              updates: {
                content: 'API request timed out after 90 seconds.',
                status: 'failed',
                updatedAt: new Date().toISOString(),
                metadata: { device: 'desktop', isThinking: false },
              },
            },
          });
          dispatch({ type: 'SET_LOADING', payload: false });
          reject(new Error('API request timed out after 90 seconds'));
        }, 90000);
      });

      // Send message to API with race against timeouts
      let response;
      try {
        response = await Promise.race([
          (async () => {
            const res = await apiService.sendMessage(chatId, content);
            if (connectionTimeout) clearTimeout(connectionTimeout);
            return res;
          })(),
          connectionPromise,
        ]);
        if (!timedOut) {
          await Promise.race([
            Promise.resolve(response),
            responsePromise,
          ]);
          if (responseTimeout) clearTimeout(responseTimeout);
        }
      } catch (err) {
        return; // Already handled by timeout logic
      }

      if (timedOut) return;
      const apiResponse = response as ApiResponse<any>;
      if (apiResponse && apiResponse.success && apiResponse.data) {
        // Update user message with server response if available. Be defensive about response shape.
        const serverMessage = (apiResponse.data && (apiResponse.data.message || apiResponse.data.msg || apiResponse.data.savedMessage)) || null;

        if (serverMessage && typeof serverMessage === 'object') {
          const serverId = serverMessage.id || serverMessage._id || messageId;
          dispatch({
            type: 'UPDATE_MESSAGE',
            payload: {
              ...optimisticMessage,
              ...serverMessage,
              id: serverId,
              status: 'sent',
            },
          });
        } else {
          // If server didn't return a message object, fall back to marking optimistic message as sent
          console.warn('sendMessage: server did not return a message object, falling back to optimistic message.', apiResponse);
          dispatch({
            type: 'UPDATE_MESSAGE',
            payload: {
              ...optimisticMessage,
              id: messageId,
              status: 'sent',
            },
          });
        }

        try {
          // Fetch latest user settings to send with query
          const settingsRes = await apiService.getUserSettings();
          const settings = settingsRes.success && settingsRes.data ? settingsRes.data.settings : undefined;

          const s = settings ?? { genAiMode: '', projects: [], llmHost: '', llmModel: '', llmApiKey: '' };
          const payload: any = {
            query: content,
            chat_id: currentChat ? currentChat.id : null,
            collections: s.projects,
            // Pass mode exactly as saved in settings (allow empty string)
            mode: s.genAiMode,
            host: s.llmHost,
            api_key: s.llmApiKey,
            model: s.llmModel,
          };

          // Try to attach current user id from localStorage if available
          try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              const parsed = JSON.parse(storedUser);
              if (parsed && parsed._id) {
                payload.user_id = parsed._id;
              } else if (parsed && parsed.id) {
                payload.user_id = parsed.id;
              }
            }
          } catch (e) {
            console.warn('Failed to read user from localStorage for payload user_id:', e);
          }

            console.log('Payload sent to /api/fastai/query:', JSON.stringify(payload, null, 2));

          const mcpResponse = await apiService.chatWithFastAI(payload);

          if (mcpResponse.success && mcpResponse.data) {
            // Handle different possible response formats from MCP Retrieval proxy
            let answer = 'No response from AI';
            let responseData = mcpResponse.data;

            // If the response is a string, try to parse it as JSON
            if (typeof responseData === 'string') {
              try {
                responseData = JSON.parse(responseData);
              } catch (e) {
                console.warn('Failed to parse FastAPI response as JSON:', e);
              }
            }

            // Extract answer from different possible response structures
            if (responseData.answer) {
              answer = responseData.answer;
            } else if (responseData.response) {
              answer = responseData.response;
            } else if (typeof responseData === 'string') {
              answer = responseData;
            } else if (responseData.data && typeof responseData.data === 'string') {
              answer = responseData.data;
            } else if (responseData.data && responseData.data.answer) {
              answer = responseData.data.answer;
            } else if (responseData.data && responseData.data.response) {
              answer = responseData.data.response;
            }

            // Ensure we have a string to display
            if (typeof answer !== 'string') {
              console.warn('Unexpected MCP Retrieval response format:', responseData);
              answer = 'Received an unexpected response format from the AI service.';
            }



            // Extract source and source_urls from responseData if present

            // Extract source, source_urls, and documents from responseData if present
            const aiMeta: any = {
              device: 'desktop',
              responseData: responseData,
            };
            if (responseData.source) aiMeta.source = responseData.source;
            if (responseData.source_urls) aiMeta.source_urls = responseData.source_urls;
            if (responseData.documents) aiMeta.documents = responseData.documents;

            // First update the thinking message with the AI response content
            dispatch({
              type: 'UPDATE_MESSAGE_BY_ID',
              payload: {
                id: aiMessageId, // Use the original temporary ID to find the message
                updates: {
                  content: answer,
                  status: 'sent',
                  tokensUsed: responseData.tokens_used || responseData.tokensUsed || 0,
                  metadata: aiMeta,
                  updatedAt: new Date().toISOString(),
                },
              },
            });

            // Persist AI response and get the real message from the server
            try {
              const saveResponse = await apiService.saveAIResponse(chatId, answer);
              if (saveResponse && saveResponse.success && saveResponse.data) {
                // Use any-cast and optional chaining to avoid TypeScript errors for unexpected shapes
                const savedData: any = saveResponse.data;
                const finalAssistantMessage = savedData?.message ?? savedData?.msg ?? null;
                if (finalAssistantMessage && typeof finalAssistantMessage === 'object') {
                  // Update with the permanent server ID if different
                  const serverId = finalAssistantMessage.id || finalAssistantMessage._id || null;
                  if (serverId && serverId !== aiMessageId) {
                    dispatch({
                      type: 'UPDATE_MESSAGE_BY_ID',
                      payload: {
                        id: aiMessageId, // Find by temporary ID
                        updates: {
                          id: serverId, // Update to permanent ID
                        },
                      },
                    });
                  }
                } else {
                  console.warn('saveAIResponse returned unexpected message shape:', saveResponse);
                }
              } else {
                // If saving fails or response shape is unexpected, mark the message as failed
                console.warn('Failed to persist AI response, marking thinking message as failed:', saveResponse);
                dispatch({
                  type: 'UPDATE_MESSAGE_BY_ID',
                  payload: {
                    id: aiMessageId,
                    updates: {
                      status: 'failed'
                    }
                  },
                });
              }
            } catch (saveErr) {
              console.error('Error saving AI response:', saveErr);
              dispatch({
                type: 'UPDATE_MESSAGE_BY_ID',
                payload: {
                  id: aiMessageId,
                  updates: {
                    status: 'failed'
                  }
                },
              });
            }
          } else {
            // Update with error state
            dispatch({
              type: 'UPDATE_MESSAGE_BY_ID',
              payload: {
                id: aiMessageId,
                updates: {
                  content: 'Failed to get response from AI service',
                  status: 'failed' as const,
                  updatedAt: new Date().toISOString(),
                  metadata: { device: 'desktop', isThinking: false },
                },
              },
            });
          }
        } catch (err) {
          console.error('Error getting AI response:', err);
          dispatch({
            type: 'UPDATE_MESSAGE_BY_ID',
            payload: {
              id: aiMessageId,
              updates: {
                content: 'Error contacting AI service',
                status: 'failed' as const,
                updatedAt: new Date().toISOString(),
                metadata: { device: 'desktop', isThinking: false },
              },
            },
          });
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        // Update message status to failed
        const apiResponse = response as ApiResponse<any>;
        const errorMessage = 'error' in apiResponse ? apiResponse.error : 'Failed to send message';
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            id: messageId,
            chat: chatId,
            sender: 'user' as const,
            content: content,
            type: 'text' as const,
            format: 'plain' as const,
            status: 'failed' as const,
            attachments: [],
            metadata: { device: 'desktop' as const },
            reactions: [],
            threadMessages: [],
            editHistory: [],
            isEdited: false,
            isDeleted: false,
            isPinned: false,
            isImportant: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            tokensUsed: 0,
          },
        });
        dispatch({
          type: 'SET_ERROR',
          payload: typeof errorMessage === 'string' ? errorMessage : 'Failed to send message',
        });
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } catch (error: any) {
      console.error('Error in sendMessage:', error);
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: {
          id: messageId,
          chat: chatId,
          sender: 'user' as const,
          content: content,
          type: 'text' as const,
          format: 'plain' as const,
          status: 'failed' as const,
          attachments: [],
          metadata: { device: 'desktop' as const },
          reactions: [],
          threadMessages: [],
          editHistory: [],
          isEdited: false,
          isDeleted: false,
          isPinned: false,
          isImportant: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          tokensUsed: 0,
        },
      });
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to send message',
      });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadMoreMessages = async (): Promise<void> => {
    if (!state.currentChat || !state.hasMore) return;

    try {
      const oldestMessage = state.messages[0];
      const response = await apiService.getMessages(state.currentChat.id, {
        limit: 50,
        before: oldestMessage?.id,
      });

      if (response.success && response.data) {
        const newMessages = response.data.messages || [];
        dispatch({ type: 'SET_MESSAGES', payload: [...newMessages, ...state.messages] });
        dispatch({ type: 'SET_HAS_MORE', payload: newMessages.length === 50 });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load more messages' });
    }
  };

  const searchChats = async (query: string): Promise<void> => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });

    if (!query.trim()) {
      dispatch({ type: 'SET_SEARCH_RESULTS', payload: [] });
      return;
    }

    try {
      const response = await apiService.searchChats(query, { limit: 20 });

      if (response.success && response.data) {
        dispatch({ type: 'SET_SEARCH_RESULTS', payload: response.data.chats });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Search failed' });
    }
  };

  const deleteChat = async (chatId: string): Promise<void> => {
    try {
      const response = await apiService.deleteChat(chatId);

      if (response.success) {
        dispatch({ type: 'REMOVE_CHAT', payload: chatId });
        toast.success('Chat deleted successfully');
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to delete chat' });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete chat' });
    }
  };

  const favoriteChat = async (chatId: string): Promise<void> => {
    try {
      const response = await apiService.favoriteChat(chatId);

      if (response.success && response.data) {
        dispatch({ type: 'UPDATE_CHAT', payload: response.data.chat });
        toast.success('Chat updated successfully');
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update chat' });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update chat' });
    }
  };

  const clearCurrentChat = () => {
    dispatch({ type: 'SET_CURRENT_CHAT', payload: null });
    dispatch({ type: 'SET_MESSAGES', payload: [] });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const reloadChats = async () => {
    await loadChats();
  };

  const value: ChatContextType = {
    ...state,
    createChat,
    selectChat,
    sendMessage,
    loadMoreMessages,
    searchChats,
    deleteChat,
    favoriteChat,
    clearCurrentChat,
    clearError,
    reloadChats,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
